import { useCallback, useEffect, useMemo, useState } from 'react';
import { useThemeEngine } from '../../../theme/ThemeEngineContext';
import type { ThemeDoc, TokenMap } from '../../../theme/types';

export const LIVE_PREVIEW_KEY = 'customize.livePreview';

export type ThemeScope = 'user' | 'org';
export type ThemeMode = 'light' | 'dark';

/**
 * Draft editing for the Customize page.
 *
 * Every edit goes straight into the engine, so the whole session repaints and
 * the user can browse real screens with their unsaved theme applied. Nothing is
 * persisted until Save or Publish.
 *
 * The scope decides which stored document the draft starts from and where it
 * lands, and it also decides who owns the kill switch: `enabled` fails closed
 * and only the ORG document may set it, so an org-scope draft has to carry the
 * flag itself or an admin building the very first theme would edit against a
 * blank screen and see nothing happen.
 */
export function useCustomizeDraft() {
  const engine = useThemeEngine();
  const [scope, setScopeState] = useState<ThemeScope>('user');
  const [mode, setMode] = useState<ThemeMode>(
    () => (typeof document !== 'undefined'
      && document.documentElement.classList.contains('dark') ? 'dark' : 'light'),
  );

  /** The stored document this scope edits. */
  const baseDoc = useMemo<ThemeDoc>(() => {
    const stored = scope === 'org' ? engine.orgTheme : engine.userTheme;
    return stored ?? { schemaVersion: 1 };
  }, [scope, engine.orgTheme, engine.userTheme]);

  /** What the editor shows: the draft if one is open, otherwise what is stored. */
  const working = engine.draft ?? baseDoc;
  const isDirty = engine.draft !== null;

  /**
   * Whether a draft is applied to the WHOLE app or confined to the preview panel.
   *
   * On is the better default - judging a theme against real screens is the point
   * - but it makes the surrounding UI move while you work, which is distracting
   * when you are tuning one value at a time. Per-browser, since it is a working
   * preference rather than part of the theme.
   */
  const [livePreview, setLivePreviewState] = useState<boolean>(() => {
    try {
      return localStorage.getItem(LIVE_PREVIEW_KEY) !== 'off';
    } catch {
      return true;
    }
  });

  const setLivePreview = useCallback((on: boolean) => {
    setLivePreviewState(on);
    try {
      localStorage.setItem(LIVE_PREVIEW_KEY, on ? 'on' : 'off');
    } catch { /* private mode - the session still honours the choice */ }
  }, []);

  /**
   * Whether theming should be ON for everyone once this org draft is published.
   *
   * Separate from what the editor previews: the preview always renders (the
   * engine forces that for any draft), while this is the choice that actually
   * reaches other people. It mirrors the CURRENT stored state rather than
   * assuming - a switch that changes how the app looks for every user should be
   * ticked deliberately, not defaulted on behind someone's back. It only starts
   * on when no org document exists at all, where there is nothing to preserve.
   */
  const [orgEnabled, setOrgEnabled] = useState(true);
  useEffect(() => {
    setOrgEnabled(engine.orgTheme?.enabled ?? true);
  }, [engine.orgTheme]);

  // The draft is always held in the engine, even with site-wide preview off:
  // it is the single source the page reads back, and the page scopes the
  // compiled CSS to its own panel in that mode. See Customize.tsx.
  const pushDraft = useCallback((next: ThemeDoc) => {
    engine.setDraft(next);
  }, [engine]);

  const setToken = useCallback((group: 'tokens' | 'components', name: string, value: string) => {
    const current = engine.draft ?? baseDoc;
    const section = current[group] ?? {};
    const forMode: TokenMap = { ...(section[mode] ?? {}) };

    if (value) forMode[name] = value;
    else delete forMode[name];      // empty means "fall back to the default"

    pushDraft({ ...current, [group]: { ...section, [mode]: forMode } });
  }, [engine.draft, baseDoc, mode, pushDraft]);

  const patch = useCallback((partial: Partial<ThemeDoc>) => {
    pushDraft({ ...(engine.draft ?? baseDoc), ...partial });
  }, [engine.draft, baseDoc, pushDraft]);

  const revert = useCallback(() => engine.setDraft(null), [engine]);

  /** Switching scope abandons the draft - the two edit different documents. */
  const setScope = useCallback((next: ThemeScope) => {
    engine.setDraft(null);
    setScopeState(next);
  }, [engine]);

  const save = useCallback(async () => {
    if (!engine.draft) return;
    if (scope === 'org') {
      // Publish the admin's explicit switch value, not the forced-on one the
      // preview runs with.
      await engine.publishOrgTheme({ ...engine.draft, enabled: orgEnabled });
    } else {
      await engine.saveUserTheme();
    }
  }, [engine, scope, orgEnabled]);

  return {
    scope, setScope, mode, setMode,
    working, isDirty,
    orgEnabled, setOrgEnabled,
    livePreview, setLivePreview,
    setToken, patch, revert, save,
    rejected: engine.rejected,
    resolved: engine.resolved,
    isLoading: engine.isLoading,
  };
}
